<template>
  <footer
    class="footer"
    :class="{ 'is-dark': isDarkMode }"
  >
    <div class="container">
      <div class="columns">
        <div class="column is-6">
          <div class="footer-logo mb-4">
            <router-link to="/">
              <img
                :src="isDarkMode ? logoDark : logoLight"
                alt="Indiefy Logo"
              >
            </router-link>
          </div>
          <p class="mb-4">
            {{ $t('general.slogan') }}
          </p>
          <div class="social-links">
            <a
              href="https://www.facebook.com/indiefyapp"
              class="mr-3"
            >
              <i class="fab fa-facebook-f" />
            </a>
            <a
              href="https://x.com/indiefyapp"
              class="mr-3"
            >
              <i class="fab fa-twitter" />
            </a>
            <a
              href="https://www.instagram.com/indiefyapp"
              class="mr-3"
            >
              <i class="fab fa-instagram" />
            </a>
            <a
              href="https://www.youtube.com/@indiefymusic"
              class="mr-3"
            >
              <i class="fab fa-youtube" />
            </a>
            <a href="https://open.spotify.com/user/0prvlevxuj0z0qizh2o7zd5hy?si=e565eabd0a7340cb">
              <i class="fab fa-spotify" />
            </a>
          </div>
        </div>

        <div class="column is-6 has-text-right">
          <h4 class="footer-title">
            {{ $t('general.language') }}
          </h4>
          <div class="buttons has-text-right">
            <router-link
              v-for="language in availableLanguages"
              :key="language.code"
              :to="`${currentPath}?lang=${language.code}`"
              class="button is-small"
              :class="{
                'is-primary': currentLanguage === language.code,
                'is-light': currentLanguage !== language.code
              }"
            >
              {{ language.name }}
            </router-link>
          </div>

          <div class="mt-4">
            <button
              class="button is-small is-light"
              @click="toggleDarkMode"
            >
              <span class="icon">
                <i :class="isDarkMode ? 'fas fa-sun' : 'fas fa-moon'" />
              </span>
              <span>{{ isDarkMode ? $t('general.light-mode') : $t('general.dark-mode') }}</span>
            </button>
            <router-link
              to="/artists-catalog"
              class="button is-small is-light"
            >
              {{ $t('navigation.artists') }}
            </router-link>
          </div>

          <div class="mt-4">
            <router-link
              to="/promotion"
              class="button is-small is-light"
            >
              Promotion
            </router-link>
            <router-link
              to="/cover-editor"
              class="button is-small is-light"
            >
              Cover Editor
            </router-link>
            <router-link
              to="/mastering"
              class="button is-small is-light"
            >
              Mastering
            </router-link>
          </div>
          <div class="mt-4">
            <a
              href="https://smartlinks.indiefy.net/"
              class="button is-small is-light"
            >
              Smart Links
            </a>
          </div>
        </div>
      </div>

      <hr class="footer-divider">

      <div class="columns">
        <div class="column">
          <p class="has-text-centered">
            &copy; {{ new Date().getFullYear() }} Indiefy. All rights reserved.
          </p>
          <p class="has-text-centered mt-2">
            <router-link
              to="/terms-and-conditions"
              class="mr-3"
            >Terms & Conditions</router-link>
            <router-link
              to="/privacy-policy"
              class="mr-3"
            >Privacy Policy</router-link>
            <router-link to="/cookie-policy">Cookie Policy</router-link>
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue';
import { useThemeStore } from '@/store/theme';
import { getAvailableLanguages } from '@/i18n';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import logoLight from '@/assets/images/logo-dark.png';
import logoDark from '@/assets/images/logo-white.png';

// Theme
const themeStore = useThemeStore();
const isDarkMode = computed(() => themeStore.isDark);

// Language
const { locale } = useI18n();
const currentLanguage = computed(() => locale.value);
const availableLanguages = getAvailableLanguages();
const currentPath = computed(() => useRoute()?.path || '');

// Methods
const toggleDarkMode = () => {
  themeStore.toggleDarkMode();
};
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/animations";

.footer {
  padding: 3rem 1.5rem;
  background-color: var(--color-background-alt, #f5f5f5);
  border-top: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
  transition: all 0.3s ease;

  .footer-logo img {
    height: 40px;
    max-width: 100%;
  }

  .buttons {
    display: inline;
  }

  &.is-dark {
    background-color: var(--color-card-background, #1a1a2e);
    color: var(--color-text, #f5f5f5);

    .footer-title {
      color: var(--color-text, white);
    }

    .footer-links a {
      color: var(--color-text-light, #e1e1e1);

      &:hover {
        color: var(--color-primary, $indiefy-teal);
      }
    }

    .social-links a {
      background-color: var(--color-background, #2a2a3a);
      color: var(--color-text-light, #e1e1e1);

      &:hover {
        background-color: var(--color-primary, $indiefy-blue);
        color: white;
      }
    }

    .footer-divider {
      background-color: var(--color-border, rgba(255, 255, 255, 0.1));
    }
  }

  .footer-title {
    font-weight: 600;
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }

  .footer-links {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 0.5rem;
    }

    a {
      color: #555;
      transition: $transition-base;

      &:hover {
        color: $indiefy-blue;
      }
    }
  }

  .footer-divider {
    margin: 2rem 0;
    background-color: rgba(0, 0, 0, 0.1);
  }

  .social-links {
    a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background-color: var(--color-background-alt, #eee);
      color: var(--color-text, #555);
      transition: all 0.3s ease;
      font-size: 0.9rem;

      &:hover {
        background-color: var(--color-primary, $indiefy-blue);
        color: white;
        transform: translateY(-3px);
      }

      &:active {
        transform: translateY(-1px);
      }
    }
  }
}
</style>